<template>
  <VacancyContainer
    v-if="true"
    :id="vacancy.id"
    v-slot="{ company, vacancy }"
    @ready="handleVacancyContainerReady"
  >
    <VRow justify="center">
      <VCol v-if="!$route.meta.iframe" cols="12" md="3" xl="2">
        <CompanySmallCard v-show="$vuetify.breakpoint.smAndDown" :company="company" />
      </VCol>
      <VCol
        cols="12"
        :md="$route.meta.iframe ? 12 :8"
        :xl="$route.meta.iframe ? 12 : 6"
        :class="{'mt-12': $route.meta.iframe}"
      >
        <VRow justify="center">
          <VCol v-if="declined" cols="12" lg="10">
            <h1 class="headline text-center">
              {{ $t('declined') }}
            </h1>
            <div class="text-center">
              <p>
                {{ $t('thanks') }}
              </p>
              <p>
                {{ $t('sorry') }}
              </p>
              <p>
                {{ $t('thanks2') }}
              </p>
            </div>
          </VCol>
          <VCol v-else-if="invited" cols="12" lg="10">
            <h1 class="headline text-center">
              {{ $t('invited') }}
            </h1>
            <div class="text-center mt-2 mt-md-0">
              {{ $t('invitedTip') }}
            </div>
          </VCol>
          <template v-else>
            <VCol cols="12" lg="10">
              <h1 class="headline text-center">
                {{ $t('success') }}
              </h1>
              <div class="text-center mt-2 mt-md-0">
                {{ $t('successTip') }}
              </div>
            </VCol>
            <VCol v-if="vacancy.faq && vacancy.faq.length > 0" cols="12" lg="10">
              <VCard elevation="1" class="pa-3 pa-md-5 px-md-12 pt-md-5 mb-5 mt-4">
                <h2 class="title font-regular">
                  {{ $t('FAQ') }} &laquo;{{ vacancy.title }}&raquo;
                </h2>
                <VacancyFaq :faq="vacancy.faq" class="mt-2" />
              </VCard>
            </VCol>
          </template>
        </VRow>
      </VCol>
    </VRow>
    <VRow v-if="!$route.meta.iframe" justify="center">
      <VCol v-show="$vuetify.breakpoint.mdAndUp" cols="12" md="3" xl="2">
        <CompanySmallCard :company="company" />
      </VCol>
      <VCol cols="12" md="8" xl="6">
        <VCard elevation="1" class="pa-3 pa-md-7 px-md-12 pt-md-5 mb-5">
          <h1 :class="$vuetify.breakpoint.mdAndUp ? 'display-2' : 'title'">
            {{ vacancy.title }}
          </h1>
          <div>
            <span>
              <VacancySalary :salary="vacancy.salary" />
            </span>
            <br>
            <span v-if="vacancy.address">
              {{ vacancy.address.city }}
            </span>
            <span v-else />
          </div>
          <VacancyCard :vacancy="vacancy" class="mt-8 body-2" />
          <template v-if="vacancy.faq && vacancy.faq.length > 0">
            <VDivider class="my-4" />
            <h2 class="title font-regular">
              {{ $t('FAQ') }} &laquo;{{ vacancy.title }}&raquo;
            </h2>
            <VacancyFaq :faq="vacancy.faq" class="mt-2" />
          </template>
        </VCard>
        <VCard v-if="filteredVacancies.length > 0" elevation="1" class="pa-7 px-md-12 mb-5">
          <h3
            class="d-flex justify-space-between align-end"
            :class="$vuetify.breakpoint.mdAndUp ? 'display-2' : 'title'"
          >
            {{ $t('otherVacancies') }} "{{ company.name }}"
            <span class="body-2 grey--text text--lighten-1 mb-1 d-none d-md-block">
              <span v-if="vacancies.isFetched">
                {{ $t('more') }} {{ $tc('vacancy', filteredVacancies.length) }}
              </span>
              <span v-else />
            </span>
          </h3>
          <VDivider class="mt-2" />
          <CompanyVacanciesContainer
            :company-id="company.id"
            :exclude="vacancy.id"
            :limit="vacanciesLimit"
          />
          <template v-show="vacancies.items.length > vacanciesLimit">
            <VDivider class="mb-5" />
            <RouterLink
              :to="{ name: 'vacancies', params: { companyId: company.id }}"
              class="subtitle-2 text-decoration-none"
            >
              <template v-if="$vuetify.breakpoint.mdAndUp">
                {{ $t('all') }} "{{ company.name }}"
              </template>
              <template v-else>
                {{ $t('more') }} {{ $tc('vacancy', filteredVacancies.length - vacanciesLimit) }}
              </template>
            </RouterLink>
          </template>
        </VCard>
        <div v-else />
      </VCol>
    </VRow>
  </VacancyContainer>
  <div v-else />
</template>

<script>
import { ref } from '@vue/composition-api';
import { mapGetters, mapState } from 'vuex';
import useLocale from '@/compositions/useLocale';
import VacancyContainer from '../components/Vacancy/VacancyContainer.vue';
import CompanyVacanciesContainer from '../components/Company/CompanyVacanciesContainer.vue';
import CompanySmallCard from '../components/Company/CompanySmallCard.vue';
import VacancyCard from '../components/Vacancy/VacancyCard.vue';
import VacancySalary from '../components/Vacancy/VacancySalary.vue';
import { candidateStatuses } from '../enums';
import { safeGet } from '../functions';
import VacancyFaq from '../components/Vacancy/VacancyFaq.vue';

export default {
  name: 'InterviewResult',
  components: {
    VacancyContainer,
    CompanyVacanciesContainer,
    CompanySmallCard,
    VacancyCard,
    VacancySalary,
    VacancyFaq,
  },
  metaInfo() {
    return {
      title: `${this.$t('applied')} ${safeGet(this.vacancy, ['title'])}`,
    };
  },
  props: {
    companyId: {
      type: Number,
      required: true,
    },
    vacancyId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      vacanciesLimit: 3,
    };
  },
  setup() {
    const { changeLocale, getPreferredLocale, saveLocaleIfEmptyStorageValue } = useLocale();

    return {
      changeLocale,
      getPreferredLocale,
      saveLocaleIfEmptyStorageValue,
      vacanciesLimit: ref(3),
    };
  },
  computed: {
    ...mapGetters('candidate', ['candidate', 'vacancy']),
    ...mapState('company', ['vacancies']),
    declined() {
      return this.candidate.status === candidateStatuses.DECLINED;
    },
    invited() {
      return this.candidate.status === candidateStatuses.INOFFICE_INTERVIEW
        || this.candidate.status === candidateStatuses.JOB_OFFER
        || this.candidate.status === candidateStatuses.HIRED;
    },
    filteredVacancies() {
      return this.vacancies.items.filter(v => v.id !== this.vacancyId);
    },
  },
  async created() {
    await this.$store.dispatch('candidate/fetchData', { vacancyId: this.vacancyId });
    if (!this.candidate || this.vacancy.id !== this.vacancyId) {
      return this.$router.push({
        name: 'vacancy',
        params: {
          vacancyId: this.vacancyId,
        },
      });
    }

    if (this.candidate.status === candidateStatuses.INVITED) {
      if (!this.candidate.interview) {
        return this.$router.push({
          name: 'vacancyReply',
          params: {
            companyId: this.vacancy.companyId,
            vacancyId: this.vacancy.id,
          },
        });
      }

      return this.$router.push({
        name: 'interviewContinue',
        params: {
          companyId: this.vacancy.companyId,
          vacancyId: this.vacancy.id,
        },
      });
    }

    this.calculateHeight();

    return null;
  },
  async serverPrefetch() {
    await this.fetch();
  },
  async beforeMount() {
    if (!this.vacancies.isFetched) {
      await this.fetch();
    }
  },
  methods: {
    async fetch() {
      await this.$store.dispatch('company/fetchVacancies', this.companyId);
    },
    handleVacancyContainerReady({ vacancy }) {
      const locale = this.getPreferredLocale() ?? vacancy.script.interviewer.locale;
      this.changeLocale(locale);
      this.saveLocaleIfEmptyStorageValue(locale);
    },
    calculateHeight() {
      this.$nextTick(() => {
        setTimeout(() => {
          let height = document.documentElement.scrollHeight;
          if (document.documentElement.offsetHeight > height) {
            height = document.documentElement.offsetHeight;
          }
          // eslint-disable-next-line no-restricted-globals
          parent.postMessage({ height }, '*');
        }, 1000);
      });
    },
  },
};
</script>

<i18n locale="en" lang="yaml">
  declined: We are sorry to inform you that your application was refused
  thanks: Thank you for your response and interest in our job
  sorry: >-
    Unfortunately, at the moment we are not ready to proceed with
    your application, as we are looking for a person whose experience and
    skills more closely meet our requirements
  thanks2: Thank you again, have a good day and good luck on your job search
  invited: You are invited to an in-person interview!
  invitedTip: The employer will get in touch with you soon
  success: You have completed the interview
  successTip: You will be contacted after your interview is reviewed
  salaryPrefix: ''
  otherVacancies: other jobs
  more: more
  all: All jobs
  applied: You have applied to the job
  vacancy: 0 jobs | {n} job | {n} jobs | {n} jobs
</i18n>

<i18n locale="ru" lang="yaml">
  declined: К сожалению, вам отказали
  thanks: Благодарим за ваш отклик и интерес, проявленный к нашей вакансии.
  sorry: >-
    К сожалению, на данный момент мы не готовы продолжить общение и пригласить
    вас на собеседование, поскольку ищем человека, чей опыт и
    умения более точно соответствуют нашим требованиям.
  thanks2: Ещё раз спасибо. Хорошего дня и, конечно же, удачи в поисках работы!
  invited: Вы приглашены на очное собеседование
  invitedTip: Работодатель свяжется с вами в ближайшее время.
  success: Интервью пройдено!
  successTip: С вами свяжутся после ознакомления с вашим интервью
  salaryPrefix: з/п
  otherVacancies: Другие вакансии компании
  more: Еще
  all: Все вакансии компании
  applied: Вы откликнулись на вакансию
  vacancy: 0 вакансий | {n} вакансия | {n} вакансии | {n} вакансий
</i18n>

<style lang="scss" scoped>
::v-deep .v-row {
  max-width: 100%;
  overflow-x: hidden;
}

::v-deep .v-col {
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}
</style>
